export interface ExperienceInsights {
  readonly requiredExp: number | null
  readonly remainingExp: number | null
  readonly etaMs: number | null
  readonly fullLevelDurationMs: number | null
}

export interface DisplayExperience {
  readonly currentExp: number | null
  readonly requiredExp: number | null
  readonly currentEstimated: boolean
  readonly requiredEstimated: boolean
}

export function deriveDisplayExperience(
  observedRequiredExp: number | null | undefined,
  ratio: number | null | undefined,
  absolute: number | null | undefined,
): DisplayExperience {
  const validRequired = observedRequiredExp !== null && observedRequiredExp !== undefined
    && Number.isFinite(observedRequiredExp) && observedRequiredExp > 0
    ? Math.round(observedRequiredExp)
    : null
  const validRatio = ratio !== null && ratio !== undefined && Number.isFinite(ratio) && ratio >= 0 && ratio <= 1
    ? ratio
    : null
  const validAbsolute = absolute !== null && absolute !== undefined && Number.isFinite(absolute) && absolute >= 0
    ? Math.round(absolute)
    : null

  const currentExp = validAbsolute ?? (validRequired !== null && validRatio !== null
    ? Math.round(validRequired * validRatio)
    : null)

  if (validAbsolute !== null && validRatio !== null && validRatio > 0) {
    const inferredRequired = Math.max(validAbsolute, Math.round(validAbsolute / validRatio))
    const observedRequirementMatches = validRequired !== null
      && Math.abs(validAbsolute / validRequired - validRatio) <= 0.0001
    return {
      currentExp,
      requiredExp: observedRequirementMatches ? validRequired : inferredRequired,
      currentEstimated: false,
      requiredEstimated: !observedRequirementMatches,
    }
  }

  return {
    currentExp,
    requiredExp: validRequired,
    currentEstimated: currentExp !== null && validAbsolute === null,
    requiredEstimated: false,
  }
}

export function deriveExperienceInsightsFromValues(
  requiredExp: number | null | undefined,
  currentExp: number | null | undefined,
  expPerHour: number | null | undefined,
): ExperienceInsights {
  if (requiredExp === null || requiredExp === undefined || !Number.isFinite(requiredExp) || requiredExp <= 0) {
    return { requiredExp: requiredExp ?? null, remainingExp: null, etaMs: null, fullLevelDurationMs: null }
  }

  const validRate = expPerHour !== null && expPerHour !== undefined && Number.isFinite(expPerHour) && expPerHour > 0
    ? expPerHour
    : null
  const fullLevelDurationMs = validRate === null ? null : requiredExp / validRate * 3_600_000
  if (currentExp === null || currentExp === undefined
    || !Number.isFinite(currentExp) || currentExp < 0 || currentExp > requiredExp) {
    return { requiredExp, remainingExp: null, etaMs: null, fullLevelDurationMs }
  }
  const remainingExp = requiredExp - currentExp
  const etaMs = remainingExp === 0
    ? 0
    : validRate !== null
      ? remainingExp / validRate * 3_600_000
      : null

  return { requiredExp, remainingExp, etaMs, fullLevelDurationMs }
}

export function ratePerHour(total: number | null | undefined, durationMs: number): number | null {
  if (total === null || total === undefined || durationMs <= 0) return null
  return total / durationMs * 3_600_000
}
